import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';

export type Language = 'en' | 'ar';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  isRTL: boolean;
  getText: (englishText: string, arabicText?: string) => string;
  getLocalizedText: (obj: { [key: string]: any }, field: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const savedLanguage = localStorage.getItem('language') as Language;
    return savedLanguage || 'ar'; // Default to Arabic
  });

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('language', lang);

    // Update document direction and language
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
  }, []);

  const isRTL = language === 'ar';

  const getText = useCallback((englishText: string, arabicText?: string): string => {
    if (language === 'ar' && arabicText) {
      return arabicText;
    }
    return englishText;
  }, [language]);

  const getLocalizedText = useCallback((obj: { [key: string]: any }, field: string): string => {
    if (language === 'ar' && obj[`${field}Ar`]) {
      return obj[`${field}Ar`];
    }
    return obj[field] || '';
  }, [language]);

  useEffect(() => {
    // Set initial document direction and language
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
    document.body.dir = language === 'ar' ? 'rtl' : 'ltr';
    document.body.lang = language;

    // Force re-render of all elements
    document.body.style.direction = language === 'ar' ? 'rtl' : 'ltr';
    document.body.style.textAlign = language === 'ar' ? 'right' : 'left';
  }, [language]);

  const value = useMemo<LanguageContextType>(() => ({
    language,
    setLanguage,
    isRTL,
    getText,
    getLocalizedText,
  }), [language, setLanguage, isRTL, getText, getLocalizedText]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
